import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('light');
  const [systemPreference, setSystemPreference] = useState('light');
  const [primaryColor, setPrimaryColor] = useState('#ea580c'); // Default orange-600

  // Detect system theme preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setSystemPreference(mediaQuery.matches ? 'dark' : 'light');

    const handleChange = (e) => {
      setSystemPreference(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Load theme and primary color from localStorage
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme && ['light', 'dark', 'system'].includes(savedTheme)) {
      setTheme(savedTheme);
    } else {
      setTheme('system');
    }

    const savedColor = localStorage.getItem('primaryColor');
    if (savedColor) {
      setPrimaryColor(savedColor);
    }
  }, []);

  // Helper to calculate foreground color based on primary color brightness
  const getContrastColor = (hexcolor) => {
    // If no hex provided, use default
    if (!hexcolor) return '#ffffff';
    
    // Remove # if present
    const hex = hexcolor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Calculate relative luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Returns black for bright colors, white for dark colors
    return luminance > 0.65 ? '#000000' : '#ffffff';
  };

  // Apply theme and primary color to document
  useEffect(() => {
    const effectiveTheme = theme === 'system' ? systemPreference : theme;
    
    if (effectiveTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    
    // Apply primary color CSS variables
    document.documentElement.style.setProperty('--color-primary', primaryColor);
    document.documentElement.style.setProperty('--color-ring', primaryColor);
    
    // Generate hover and light variants using color-mix
    document.documentElement.style.setProperty('--color-primary-hover', `color-mix(in srgb, ${primaryColor}, black 10%)`);
    document.documentElement.style.setProperty('--color-primary-light', `color-mix(in srgb, ${primaryColor}, white 90%)`);
    
    // Translucent variants for glassmorphism
    document.documentElement.style.setProperty('--color-primary-10', `color-mix(in srgb, ${primaryColor}, transparent 90%)`);
    document.documentElement.style.setProperty('--color-primary-20', `color-mix(in srgb, ${primaryColor}, transparent 80%)`);
    document.documentElement.style.setProperty('--color-primary-50', `color-mix(in srgb, ${primaryColor}, transparent 50%)`);
    
    // Dynamic foreground color based on contrast
    const foregroundColor = getContrastColor(primaryColor);
    document.documentElement.style.setProperty('--color-primary-foreground', foregroundColor);

    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      // Set meta color to effective background color
      const bgColor = effectiveTheme === 'dark' ? '#0f172a' : '#f8fafc';
      metaThemeColor.setAttribute('content', bgColor);
    }
  }, [theme, systemPreference, primaryColor]);

  const toggleTheme = () => {
    const themes = ['light', 'dark', 'system'];
    const currentIndex = themes.indexOf(theme);
    const nextTheme = themes[(currentIndex + 1) % themes.length];
    setTheme(nextTheme);
    localStorage.setItem('theme', nextTheme);
  };

  const setSpecificTheme = (newTheme) => {
    if (['light', 'dark', 'system'].includes(newTheme)) {
      setTheme(newTheme);
      localStorage.setItem('theme', newTheme);
    }
  };

  const setSpecificPrimaryColor = (color) => {
    setPrimaryColor(color);
    localStorage.setItem('primaryColor', color);
  };

  const getEffectiveTheme = () => {
    return theme === 'system' ? systemPreference : theme;
  };

  const value = {
    theme,
    effectiveTheme: getEffectiveTheme(),
    systemPreference,
    primaryColor,
    toggleTheme,
    setTheme: setSpecificTheme,
    setPrimaryColor: setSpecificPrimaryColor,
    isDark: getEffectiveTheme() === 'dark'
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
